import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { X } from 'lucide-react'
import {
    Checkin,
    CollectionItemWithCollection,
    getCheckins,
} from '../../api/client'
import CheckinItem from '../CheckinItem'

/**
 * Minimal, generic "check-ins referencing this item" peek for collections that
 * aren't People or Places (those reuse PersonDetailModal / LocationDetailModal).
 * Same read path as the system modals — the single-item checkins fetch
 * (`getCheckins(..., collectionItemId)`) that backs the existing detail modals.
 *
 * Deliberately non-destructive: it only reads. It is rendered as an overlay by
 * the merge flow so a user can inspect a candidate's check-ins WITHOUT losing
 * their in-progress selection — it never navigates away or mutates selection.
 */

const RECENT_LIMIT = 5

interface CollectionItemPeekModalProps {
    open: boolean
    collectionItemId: number | null
    /** Human label for the header (e.g. the item's display name). */
    title: string
    onClose: () => void
}

const CollectionItemPeekModal: React.FC<CollectionItemPeekModalProps> = ({
    open,
    collectionItemId,
    title,
    onClose,
}) => {
    const [recent, setRecent] = useState<Checkin[]>([])
    const [recentCollectionItems, setRecentCollectionItems] = useState<
        Record<string, CollectionItemWithCollection>
    >({})
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!open || collectionItemId == null) {
            setRecent([])
            setRecentCollectionItems({})
            setError(null)
            return
        }
        const itemId = collectionItemId
        let cancelled = false
        setLoading(true)
        setError(null)
        getCheckins(undefined, undefined, RECENT_LIMIT, undefined, 'most_recent', undefined, undefined, itemId)
            .then((response) => {
                if (cancelled) return
                setRecent(response.checkins)
                setRecentCollectionItems(response.collection_items || {})
            })
            .catch((err) => {
                if (cancelled) return
                console.error('Failed to load recent checkins for item:', err)
                setError('Failed to load recent checkins.')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [open, collectionItemId])

    if (!open || collectionItemId == null) return null

    return (
        <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000]"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-xl flex flex-col w-[640px] max-w-[95vw] max-h-[90vh] overflow-hidden"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <h3 className="text-sm font-medium text-gray-900 truncate pr-4">
                        {title || 'Item'}
                    </h3>
                    <motion.button
                        onClick={onClose}
                        whileHover={{ scale: 1.1, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        className="text-gray-400 hover:text-gray-600 shrink-0"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </motion.button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    <div className="px-4 py-4">
                        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                            Recent checkins
                        </h4>
                        {loading ? (
                            <div className="flex justify-center py-6">
                                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-500" />
                            </div>
                        ) : error ? (
                            <p className="text-sm text-rose-500">{error}</p>
                        ) : recent.length === 0 ? (
                            <p className="text-sm text-gray-500 italic">No checkins yet for this item.</p>
                        ) : (
                            <div>
                                {recent.map((c, idx) => (
                                    <CheckinItem
                                        key={c.id}
                                        checkin={c}
                                        index={idx}
                                        showSeparator={idx > 0}
                                        editingCheckinId={null}
                                        showNewCheckinComposer={false}
                                        features={[]}
                                        recentFeatures={[]}
                                        sortOrder="most_recent"
                                        savingState={null}
                                        showMenu={false}
                                        showMap={false}
                                        collectionItems={recentCollectionItems}
                                        formatDate={(d) => new Date(d).toLocaleDateString()}
                                        getComposerCheckinFromCheckin={() => ({ body: '', applies_to: '', features: [], projects: [] })}
                                        handleFieldEdit={() => {}}
                                        handleDeleteCheckin={() => {}}
                                        handleEditCheckin={() => {}}
                                        handleDuplicateCheckin={() => {}}
                                        handleFilterSelect={() => {}}
                                        handleReanalyzeCheckin={() => {}}
                                        setEditingCheckinId={() => {}}
                                        setCheckins={() => {}}
                                        setShowNewCheckinComposer={() => {}}
                                        firstElementIsEmptyCheckin={() => false}
                                        isSelectionMode={false}
                                        isSelected={false}
                                        onToggleSelection={() => {}}
                                    />
                                ))}
                            </div>
                        )}
                        <div className="mt-3">
                            <Link
                                to={`/checkins?collection_item=${collectionItemId}`}
                                onClick={onClose}
                                className="text-sm text-amber-800 hover:text-amber-900"
                            >
                                View all checkins →
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2 flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 text-sm bg-action text-plum rounded hover:bg-action-dark"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    )
}

export default CollectionItemPeekModal
